import type { CSSProperties } from 'react';

export function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

export function toOptionalPositiveInteger(value: unknown): number | undefined {
  const parsedValue = typeof value === 'string' ? Number(value) : value;
  return isPositiveInteger(parsedValue) ? parsedValue : undefined;
}

export function toRequiredPositiveInteger(value: unknown, fieldName: string): number {
  const parsedValue = toOptionalPositiveInteger(value);
  if (parsedValue === undefined) {
    throw new Error(`${fieldName} must be a positive integer`);
  }
  return parsedValue;
}

// Match HTML email-input syntax even when signup opts out of native validation.
// https://html.spec.whatwg.org/multipage/input.html#email-state-(type=email)
export function isValidEmailAddress(emailAddress: string): boolean {
  return /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(emailAddress.trim());
}

export function toOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? undefined : parsedValue;
}

export function toRequiredNumber(value: unknown, fieldName: string): number {
  if (value === null || value === undefined || value === '') {
    throw new Error(`${fieldName} is required`);
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    throw new Error(`${fieldName} must be a valid number`);
  }

  return parsedValue;
}

export function toRequiredDate(value: unknown, fieldName: string): Date {
  if (value === null || value === undefined || value === '') {
    throw new Error(`${fieldName} is required`);
  }

  const parsedDate = new Date(value as string | number | Date);
  if (Number.isNaN(parsedDate.getTime())) {
    throw new Error(`${fieldName} must be a valid date`);
  }

  return parsedDate;
}

export function formatDateTimeUS(value: Date): string {
  if (Number.isNaN(value.getTime())) {
    throw new Error('value must be a valid date');
  }

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(value);

  return formattedDate.replace(', ', ' ');
}

export function formatLastUpdated(value: Date): string {
  const isToday = new Date().toDateString() === value.toDateString();
  const timeText = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(value);

  if (isToday) {
    return `Today ${timeText}`;
  }

  const dateText = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(value);
  return `${dateText} ${timeText}`;
}

export interface ScrollThumbState {
  top: number;
  isVisible: boolean;
}

export function getScrollThumbState(
  scrollElement: Pick<HTMLElement, 'scrollHeight' | 'clientHeight' | 'scrollTop'> | null,
  thumbHeight: number
): ScrollThumbState {
  if (!scrollElement || scrollElement.scrollHeight <= scrollElement.clientHeight) {
    return { top: 0, isVisible: false };
  }

  const maxScrollTop = scrollElement.scrollHeight - scrollElement.clientHeight;
  const maxThumbTop = Math.max(0, scrollElement.clientHeight - thumbHeight);
  const top = maxScrollTop > 0
    ? Math.round((scrollElement.scrollTop / maxScrollTop) * maxThumbTop)
    : 0;

  return { top, isVisible: true };
}

export function areScrollThumbStatesEqual(
  currentState: ScrollThumbState,
  nextState: ScrollThumbState
): boolean {
  return currentState.top === nextState.top && currentState.isVisible === nextState.isVisible;
}

export function parseJson<T>(value: string): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    throw new Error('Failed to parse JSON string');
  }
}

export function stringifyJson(value: unknown): string {
  return JSON.stringify(value);
}

interface AngularInjectorLike {
  get: (serviceName: string) => unknown;
}

interface AngularElementWithInjector {
  injector?: () => AngularInjectorLike;
}

interface AngularLike {
  element: (target: unknown) => AngularElementWithInjector;
}

function getAngularInjector(): AngularInjectorLike | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const angularWindow = window as Window & { angular?: AngularLike };
  const angular = angularWindow.angular;
  if (!angular) {
    return null;
  }

  const elementWithInjector = angular.element(document.body);
  return elementWithInjector.injector?.() || null;
}

export function getAngularService<T>(serviceName: string): T | null {
  const injector = getAngularInjector();
  if (!injector) {
    return null;
  }

  try {
    return injector.get(serviceName) as T;
  } catch {
    return null;
  }
}

export function getAngularWebSocketClient(): unknown | null {
  return getAngularService<unknown>('WebSocket');
}

export function createRealtimeServiceFromAngularWebSocket<T>(
  ServiceConstructor: new (webSocketClient: unknown) => T
): T | null {
  const webSocketClient = getAngularWebSocketClient();
  if (!webSocketClient) {
    return null;
  }

  try {
    return new ServiceConstructor(webSocketClient);
  } catch {
    return null;
  }
}

export function getMaskedIconStyle(cssVariableName: string, src: string): CSSProperties {
  return {
    [cssVariableName]: `url("${src}")`,
  } as CSSProperties;
}
